import { zodResolver } from '@hookform/resolvers/zod'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { clsx } from 'clsx'
import { useId } from 'react'
import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import * as Box from ':/_console/js/components/Box.tsx'
import nvidia from '{assets}/images/customers4/nvidia.svg'
import nytimes from '{assets}/images/customers4/nytimes.svg'
import oracle from '{assets}/images/customers4/oracle.svg'
import logo from '{assets}/images/logo-default.svg'
import brianHiltz from '{assets}/images/testimonials/brian_hiltz@2x.jpg'
import checkmark from '{cImg}/checkmark-signup.svg'
import { AnimatedNotification } from '~/components/AsyncAnimatedNotification.tsx'
import AsyncButton from '~/components/AsyncButton.tsx'
import { Button } from '~/components/Button.tsx'
import * as CenteredBox from '~/components/CenteredBox.tsx'
import { Form, FormGroup } from '~/components/form/primitives.tsx'
import TextInput from '~/components/form/TextInput.tsx'
import { GitHubIcon } from '~/components/icons/github.tsx'
import { Text } from '~/components/Text.tsx'

import { useSignUpUser } from './data.ts'
import { useSignupStore } from './store.ts'
import styles from './styles.module.scss'
import FlashMessages from '../flash-messages/FlashMessages.tsx'
import useFlashMessages from '../flash-messages/useFlashMessages.ts'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(6).max(72),
  confirm_field: z.string(),
})

type FieldValues = z.infer<typeof schema>

const Signup = () => {
  const flashMessages = useFlashMessages()
  const emailId = useId()
  const passwordId = useId()
  const signUpUser = useSignUpUser()
  const setEmail = useSignupStore((state) => state.setEmail)

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<FieldValues>({
    defaultValues: {
      email: '',
      password: '',
      confirm_field: '',
    },
    resolver: zodResolver(schema),
  })

  return (
    <main className={styles.signupWrapper}>
      <Helmet>
        <title>Create your Transloadit account | Transloadit</title>
      </Helmet>

      <div className={styles.signupHeader}>
        <div>
          <a href="/">
            <img src={logo} width="140" height="35" alt="Transloadit logo" />
          </a>
        </div>
      </div>

      <div className={styles.signupColumns}>
        <div className={styles.signupColumnPrimary}>
          <CenteredBox.Section>
            <CenteredBox.Header>
              <h1 className={styles.pageTitle}>Create your account</h1>
              <div className={styles.signupPageFormSubheader}>
                Set up your file processing in minutes
              </div>
            </CenteredBox.Header>

            <Box.Outer className="box--well-rounded">
              <Box.Content>
                <Box.Section>
                  <Form
                    onSubmit={handleSubmit((data) => {
                      const { email, password, confirm_field } = data
                      setEmail(email)
                      signUpUser.execute(email, confirm_field, password)
                    })}
                  >
                    <FlashMessages flashMessages={flashMessages} />

                    <AnimatedNotification danger small showNotification={!!errors.root}>
                      {errors.root?.message}
                    </AnimatedNotification>

                    <AnimatedNotification
                      danger
                      small
                      showNotification={signUpUser.status === 'error'}
                    >
                      {signUpUser.error?.message}
                    </AnimatedNotification>

                    <AnimatedNotification
                      success
                      small
                      showNotification={signUpUser.status === 'success'}
                    >
                      {signUpUser.result?.meta.message}
                    </AnimatedNotification>

                    <Button
                      asChild
                      size="large"
                      variant="github"
                      className={styles.fullWidthButton}
                    >
                      <a href="/signin-with-github/">
                        <GitHubIcon aria-hidden />
                        Log in with GitHub
                      </a>
                    </Button>

                    <div className={styles.separator}>or</div>

                    <FormGroup className={styles.formGroup}>
                      <VisuallyHidden>
                        <label htmlFor={emailId}>Email</label>
                      </VisuallyHidden>
                      <TextInput
                        {...register('email')}
                        error={errors.email}
                        id={emailId}
                        placeholder="Email"
                        type="email"
                        required
                        naked
                        large
                      />

                      <TextInput
                        {...register('confirm_field')}
                        placeholder="Email"
                        type="email"
                        naked
                        hidden
                      />
                    </FormGroup>

                    <FormGroup className={styles.formGroup}>
                      <VisuallyHidden>
                        <label htmlFor={passwordId}>Password</label>
                      </VisuallyHidden>
                      <TextInput
                        {...register('password')}
                        error={errors.password}
                        id={passwordId}
                        placeholder="Password"
                        type="password"
                        minLength={6}
                        maxLength={72}
                        required
                        naked
                        large
                      />
                    </FormGroup>

                    <AsyncButton
                      status={signUpUser.status}
                      className={clsx('btn', 'btn--primary', 'btn--large', styles.fullWidthButton)}
                      type="submit"
                    >
                      Continue
                    </AsyncButton>
                  </Form>
                </Box.Section>
              </Box.Content>
            </Box.Outer>

            <div className={styles.checkmarks}>
              <div className={styles.checkmark}>
                <img
                  className={styles.checkmarkImg}
                  src={checkmark}
                  width="16"
                  height="16"
                  alt="Checkmark icon"
                />
                Try all features for free
              </div>
              <div className={styles.checkmark}>
                <img
                  className={styles.checkmarkImg}
                  src={checkmark}
                  width="16"
                  height="16"
                  alt="Checkmark icon"
                />
                No credit card required
              </div>
            </div>

            <CenteredBox.Extra>
              <Text color="gray700">
                Already have an account? <Link to="/c/login/">Log in ›</Link>
              </Text>
            </CenteredBox.Extra>
          </CenteredBox.Section>
        </div>

        <div className={styles.signupColumnSecondary}>
          <div className={styles.testimonial}>
            <blockquote className={styles.testimonialText}>
              After nearly a week of testing various tools,{' '}
              <strong>Transloadit stood out as the clear winner.</strong> The speed and performance
              exceeded expectations, effortlessly handling hundreds of thousands of files.
            </blockquote>
            <div className={styles.testimonialAuthor}>
              <img
                className={styles.testimonialAvatar}
                src={brianHiltz}
                width={32}
                height={32}
                alt="Brian Hiltz"
              />
              <span className={styles.testimonialAuthorDetails}>Brian Hiltz, Web Developer</span>
            </div>
          </div>

          <div className={styles.trustedBy}>
            <div className={styles.trustedByCaption}>Trusted by the companies you know:</div>
            <ul className={styles.customerLogos}>
              <li>
                <img src={nytimes} alt="The New York Times" />
              </li>

              <li>
                <img src={nvidia} alt="NVIDIA" />
              </li>

              <li>
                <img src={oracle} alt="Oracle" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  )
}

export { Signup as Component }
